import {
  Text,
  IconReceiptBill,
  IconCircleX,
  Button,
  GreyGrey70,
  Link,
  Primary13Regular,
  Primary13SemiBold
} from '@sequencehq/core-components'

import { toMoney } from '@sequencehq/utils'

import { InvoicePreviewCard, NodeTimeline } from 'Automations/components'
import { useManualTask } from 'Automations/watchtower/inbox/manualTasks/hooks/useManualTask'
import { invoicePaths } from 'Invoices/invoices.router'
import { TaskInput } from './types'
import { useInvoiceData } from '../../hooks/useInvoiceData'
import {
  getTaxErrorReason,
  formatProviderName,
  getTaxErrorResolution
} from './taxErrorMessages'

export const InvoiceTaxErrorPreview = (props: { taskId: string }) => {
  const { manualTask } = useManualTask({ taskId: props.taskId })
  const input = manualTask?.input as TaskInput | undefined
  const { invoice, customer, invoiceDetails } = useInvoiceData(input?.invoiceId)

  const hasInvoiceData = invoice && customer && invoiceDetails

  const errorReason = input?.errorType
    ? getTaxErrorReason(input.errorType)
    : undefined

  const providerName = input?.taxProvider
    ? formatProviderName(input.taxProvider)
    : undefined

  const resolution =
    input?.errorType && input?.taxProvider
      ? getTaxErrorResolution(
          input.errorType,
          input.taxProvider,
          input.taxCategoryName
        )
      : undefined

  return (
    <>
      <NodeTimeline.Item icon={<IconReceiptBill />} showStem>
        <NodeTimeline.Title>
          <NodeTimeline.UserAction
            label="Invoice created"
            timestamp={invoice?.createdAt}
          />
        </NodeTimeline.Title>

        {hasInvoiceData && (
          <InvoicePreviewCard
            invoice={invoice}
            customer={customer}
            invoiceDetails={invoiceDetails}
            startCollapsed
          />
        )}
      </NodeTimeline.Item>

      <NodeTimeline.Item icon={<IconCircleX />} showStem={false} highlightIcon>
        <NodeTimeline.Title>
          <NodeTimeline.UserAction
            label={
              <Text as="span" {...Primary13SemiBold} color={GreyGrey70}>
                Invoice tax error
              </Text>
            }
            timestamp={manualTask?.createdAt}
          />
        </NodeTimeline.Title>

        {hasInvoiceData && errorReason && providerName && (
          <>
            <Text {...Primary13Regular} color={GreyGrey70} marginBottom="16px">
              Invoice{' '}
              <Text as="span" {...Primary13SemiBold} color={GreyGrey70}>
                {invoice.invoiceNumber}
              </Text>{' '}
              for{' '}
              <Text as="span" {...Primary13SemiBold} color={GreyGrey70}>
                {customer.legalName}
              </Text>{' '}
              (
              {toMoney({
                value: invoice.grossTotal,
                currency: invoice.currency
              })}
              ) could not be finalized due to{' '}
              <Text as="span" {...Primary13SemiBold} color={GreyGrey70}>
                {errorReason}
              </Text>{' '}
              with the{' '}
              <Text as="span" {...Primary13SemiBold} color={GreyGrey70}>
                {providerName}
              </Text>{' '}
              integration.
            </Text>

            {resolution && (
              <Text
                {...Primary13Regular}
                color={GreyGrey70}
                marginBottom="16px"
              >
                {resolution}
              </Text>
            )}

            <InvoicePreviewCard
              invoice={invoice}
              customer={customer}
              invoiceDetails={invoiceDetails}
              action={
                <Button
                  as={Link.Unstyled}
                  to={invoicePaths.edit(invoice.id)}
                  variant="secondary"
                  style={{ flex: 1 }}
                  data-testid="watchtower.notification.invoiceTaxError.viewInvoice"
                >
                  View invoice
                </Button>
              }
            />
          </>
        )}
      </NodeTimeline.Item>
    </>
  )
}
